import { useMemo } from 'react';
import {
  createKeyframe,
  isScaleLinked,
  syncLinkedScaleKeyframes,
  type AnimatableProperty,
} from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel } from '../controls/PanelShell';

const PROPS_ALL: AnimatableProperty[] = [
  'opacity',
  'positionX',
  'positionY',
  'scaleX',
  'scaleY',
  'rotation',
  'exposure',
  'saturation',
];

export function KeyframesPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const updateClipProperties = useEditorStore((s) => s.updateClipProperties);

  const clip = useMemo(() => {
    const selected = selection
      .map((id) => project.clips.find((c) => c.id === id))
      .find((c) => c != null);
    if (selected) return selected;
    const videoTrackIds = new Set(
      project.tracks.filter((t) => t.kind === 'video').map((t) => t.id)
    );
    const under = project.clips.filter(
      (c) =>
        videoTrackIds.has(c.trackId) &&
        playhead >= c.start &&
        playhead < c.start + c.duration
    );
    return under[under.length - 1];
  }, [project.clips, project.tracks, selection, playhead]);

  if (!clip) {
    return (
      <PanelShell title="Keyframes">
        <p className="p-3 text-2xs text-text-low">
          Select a clip or park the playhead on one.
        </p>
      </PanelShell>
    );
  }

  const localTime = Math.max(0, playhead - clip.start);
  const kfs = clip.properties.keyframes;
  const scaleLinked = isScaleLinked(clip.properties);
  const addProps = scaleLinked
    ? PROPS_ALL.filter((p) => p !== 'scaleY')
    : PROPS_ALL;

  const addKf = (property: AnimatableProperty) => {
    let value = 0;
    const t = clip.properties.transform;
    const c = clip.properties.color;
    switch (property) {
      case 'opacity':
        value = t.opacity;
        break;
      case 'positionX':
        value = t.positionX;
        break;
      case 'positionY':
        value = t.positionY;
        break;
      case 'scaleX':
        value = scaleLinked ? (t.scaleX + t.scaleY) / 2 : t.scaleX;
        break;
      case 'scaleY':
        value = t.scaleY;
        break;
      case 'rotation':
        value = t.rotation;
        break;
      case 'exposure':
        value = c.exposure;
        break;
      case 'saturation':
        value = c.saturation;
        break;
      default:
        value = 0;
    }

    let next = [
      ...kfs.filter((k) => !(k.property === property && Math.abs(k.time - localTime) < 0.001)),
      createKeyframe(property, localTime, value),
    ];
    if (scaleLinked && property === 'scaleX') {
      next = syncLinkedScaleKeyframes(next);
    }
    updateClipProperties(clip.id, { keyframes: next });
  };

  const removeKf = (id: string) => {
    const target = kfs.find((k) => k.id === id);
    let next = kfs.filter((k) => k.id !== id);
    if (scaleLinked && target?.property === 'scaleX') {
      next = next.filter(
        (k) =>
          !(k.property === 'scaleY' && Math.abs(k.time - target.time) < 0.001)
      );
    } else if (scaleLinked && target?.property === 'scaleY') {
      next = next.filter(
        (k) =>
          !(k.property === 'scaleX' && Math.abs(k.time - target.time) < 0.001)
      );
    }
    updateClipProperties(clip.id, { keyframes: next });
  };

  const visibleKfs = scaleLinked
    ? kfs.filter((k) => k.property !== 'scaleY')
    : kfs;

  const propLabel = (p: string) => {
    if (scaleLinked && p === 'scaleX') return 'Scale';
    return p;
  };

  return (
    <PanelShell title="Keyframes">
      <SectionLabel>Add at playhead ({localTime.toFixed(2)}s)</SectionLabel>
      <div className="flex flex-wrap gap-1 p-2">
        {addProps.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => addKf(p)}
            className="border border-hairline bg-raised px-2 py-1 text-2xs text-text-low hover:text-accent"
          >
            {propLabel(p)}
          </button>
        ))}
      </div>
      <SectionLabel>Existing</SectionLabel>
      <ul className="divide-y divide-hairline">
        {visibleKfs.length === 0 && (
          <li className="p-3 text-2xs text-text-low">No keyframes yet.</li>
        )}
        {[...visibleKfs]
          .sort((a, b) => a.time - b.time)
          .map((k) => (
            <li key={k.id} className="flex items-center justify-between px-2 py-1.5 text-2xs">
              <span className="timecode text-text-high">
                {propLabel(k.property)} @ {k.time.toFixed(2)}s = {k.value.toFixed(2)}
              </span>
              <button
                type="button"
                className="text-text-low hover:text-danger"
                onClick={() => removeKf(k.id)}
              >
                Remove
              </button>
            </li>
          ))}
      </ul>
    </PanelShell>
  );
}
